import React from 'react';
import { LucideIcon } from 'lucide-react';

interface StatsCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  color?: 'blue' | 'green' | 'purple' | 'orange' | 'red';
  subtext?: string;
}

const colorStyles = {
  blue: {
    bg: 'bg-gradient-to-br from-blue-50 to-indigo-50/50',
    border: 'border-blue-100/50',
    iconBg: 'bg-gradient-to-br from-blue-500 to-indigo-600',
    iconText: 'text-white',
    shadow: 'shadow-blue-500/10'
  },
  green: {
    bg: 'bg-gradient-to-br from-emerald-50 to-teal-50/50',
    border: 'border-emerald-100/50',
    iconBg: 'bg-gradient-to-br from-emerald-400 to-teal-500',
    iconText: 'text-white',
    shadow: 'shadow-emerald-500/10'
  },
  purple: {
    bg: 'bg-gradient-to-br from-fuchsia-50 to-purple-50/50',
    border: 'border-purple-100/50',
    iconBg: 'bg-gradient-to-br from-fuchsia-500 to-purple-600',
    iconText: 'text-white',
    shadow: 'shadow-purple-500/10'
  },
  orange: {
    bg: 'bg-gradient-to-br from-orange-50 to-amber-50/50',
    border: 'border-orange-100/50',
    iconBg: 'bg-gradient-to-br from-orange-400 to-amber-500',
    iconText: 'text-white',
    shadow: 'shadow-orange-500/10'
  },
  red: {
    bg: 'bg-gradient-to-br from-rose-50 to-red-50/50',
    border: 'border-rose-100/50',
    iconBg: 'bg-gradient-to-br from-rose-500 to-red-600',
    iconText: 'text-white',
    shadow: 'shadow-rose-500/10'
  },
};

export default function StatsCard({ title, value, icon: Icon, color = 'blue', subtext }: StatsCardProps) {
  const style = colorStyles[color];

  return (
    <div className={`relative overflow-hidden rounded-2xl border ${style.border} ${style.bg} p-3.5 sm:p-6 transition-all duration-300 hover:shadow-xl ${style.shadow} hover:-translate-y-1 group flex flex-col justify-between`}>
      {/* Decorative background blur */}
      <div className={`absolute -right-6 -top-6 w-24 h-24 ${style.iconBg} rounded-full blur-3xl opacity-10 group-hover:opacity-20 transition-opacity duration-500`} />
      
      <div className="relative z-10 flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-4 h-full">
        <div className="min-w-0 flex-1 order-2 sm:order-1">
          <p className="text-[10px] sm:text-sm font-bold text-slate-500 uppercase tracking-wide mb-1 sm:mb-2">{title}</p>
          <div className="flex items-baseline gap-2">
            <p className="text-2xl sm:text-4xl font-black text-slate-800 leading-none tracking-tight break-words">{value}</p>
          </div>
          {subtext && (
            <p className="text-[10px] sm:text-xs font-medium text-slate-400 mt-1.5 sm:mt-2 line-clamp-2 leading-snug">{subtext}</p>
          )}
        </div>
        <div className={`self-start p-2.5 sm:p-3.5 rounded-xl shrink-0 ${style.iconBg} ${style.iconText} shadow-md order-1 sm:order-2 mb-2 sm:mb-0`}>
          <Icon className="w-4 h-4 sm:w-6 sm:h-6" />
        </div>
      </div>
    </div>
  );
}
